import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, FileText, AlertCircle, TrendingUp, Calendar, Send } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const [stats, setStats] = useState({
    notesThisWeek: 0,
    notesToday: 0,
    draftNotes: [],
    recentNotes: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/operations/delivery-notes`, { 
        credentials: 'include' 
      });
      const allNotes = await res.json();

      // Calculate this week (Monday to Sunday)
      const now = new Date();
      const dayOfWeek = now.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = new Date(now);
      monday.setDate(now.getDate() + mondayOffset);
      monday.setHours(0, 0, 0, 0);
      
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      sunday.setHours(23, 59, 59, 999);

      // Calculate today
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);

      // Filter notes
      const notesThisWeek = allNotes.filter(note => {
        const noteDate = new Date(note.created_at);
        return noteDate >= monday && noteDate <= sunday;
      });

      const notesToday = allNotes.filter(note => {
        const noteDate = new Date(note.created_at);
        return noteDate >= todayStart && noteDate <= todayEnd;
      });

      const draftNotes = allNotes.filter(note => 
        !note.status || note.status === 'draft'
      );

      // Sort by created_at descending and get last 5
      const recentNotes = [...allNotes]
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);

      setStats({
        notesThisWeek: notesThisWeek.length,
        notesToday: notesToday.length,
        draftNotes,
        recentNotes
      });
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusDisplay = (note) => {
    if (!note.status || note.status === 'draft') {
      return { label: 'Draft', color: 'bg-gray-100 text-gray-800' };
    }
    if (note.dispatch_method === 'print') {
      return { label: 'Printed', color: 'bg-blue-100 text-blue-800' };
    }
    if (note.dispatch_method === 'email') {
      return { label: 'Emailed', color: 'bg-green-100 text-green-800' };
    }
    if (note.dispatch_method === 'print_and_email') {
      return { label: 'Printed & Emailed', color: 'bg-purple-100 text-purple-800' };
    }
    return { label: 'Sent', color: 'bg-green-100 text-green-800' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: '"Cinzel", serif' }}>
          Dashboard
        </h1>
        <Link
          to="/operations/delivery-notes/new"
          className="flex items-center px-6 py-3 bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332] transition-colors shadow-md hover:shadow-lg text-lg"
        >
          <Plus size={24} className="mr-2" />
          Create New Delivery Note
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Notes This Week</p>
              <p className="text-4xl font-bold text-gray-800 mt-2">{stats.notesThisWeek}</p>
              <p className="text-xs text-gray-400 mt-1">Monday - Sunday</p>
            </div>
            <div className="bg-blue-100 p-4 rounded-lg">
              <TrendingUp size={28} className="text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Notes Today</p>
              <p className="text-4xl font-bold text-gray-800 mt-2">{stats.notesToday}</p>
              <p className="text-xs text-gray-400 mt-1">{new Date().toLocaleDateString()}</p>
            </div>
            <div className="bg-green-100 p-4 rounded-lg">
              <Calendar size={28} className="text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Draft Notes</p>
              <p className="text-4xl font-bold text-gray-800 mt-2">{stats.draftNotes.length}</p>
              <p className="text-xs text-gray-400 mt-1">Awaiting dispatch</p>
            </div>
            <div className="bg-orange-100 p-4 rounded-lg">
              <FileText size={28} className="text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Draft Notes Alert */}
      {stats.draftNotes.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 mb-8">
          <div className="flex items-start">
            <AlertCircle size={24} className="text-orange-600 mr-3 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-orange-900 mb-2">
                {stats.draftNotes.length} Draft {stats.draftNotes.length === 1 ? 'Note' : 'Notes'} Awaiting Dispatch
              </h3>
              <p className="text-orange-800 text-sm mb-3">
                The following delivery notes have been created but not sent. Please follow up:
              </p>
              <div className="space-y-2">
                {stats.draftNotes.map(note => (
                  <Link
                    key={note.id}
                    to={`/operations/delivery-notes/${note.id}`}
                    className="block bg-white rounded-lg p-3 hover:shadow-md transition-shadow border border-orange-200"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-semibold text-gray-900">Note #{note.note_number}</span>
                        <span className="text-gray-600 mx-2">·</span>
                        <span className="text-gray-700">{note.customer_name}</span>
                        <span className="text-gray-600 mx-2">·</span>
                        <span className="text-gray-600 text-sm">{note.ref_number}</span>
                      </div>
                      <span className="text-[#1F2D3D] text-sm font-medium">View →</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Delivery Notes */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Recent Delivery Notes</h2>
          <Link 
            to="/operations/delivery-notes" 
            className="flex items-center text-[#1F2D3D] hover:underline text-sm font-medium"
          >
            View All History →
          </Link>
        </div>
        
        {stats.recentNotes.length === 0 ? (
          <div className="text-center py-12">
            <FileText size={48} className="text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 mb-4">No delivery notes yet</p>
            <Link
              to="/operations/delivery-notes/new"
              className="inline-flex items-center px-4 py-2 bg-[#1F2D3D] text-white rounded-lg hover:bg-[#1A2332] transition-colors"
            >
              <Plus size={18} className="mr-2" />
              Create Your First Note
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 text-gray-600 font-medium">Note Number</th>
                  <th className="text-left py-3 px-4 text-gray-600 font-medium">Customer</th>
                  <th className="text-left py-3 px-4 text-gray-600 font-medium">Delivery Date</th>
                  <th className="text-left py-3 px-4 text-gray-600 font-medium">Status</th>
                  <th className="text-right py-3 px-4 text-gray-600 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentNotes.map((note) => {
                  const status = getStatusDisplay(note);
                  return (
                    <tr key={note.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4">
                        <Link 
                          to={`/operations/delivery-notes/${note.id}`} 
                          className="text-[#1F2D3D] hover:underline font-medium"
                        >
                          #{note.note_number}
                        </Link>
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-medium text-gray-900">{note.customer_name}</div>
                        <div className="text-xs text-gray-500">{note.customer_code}</div>
                      </td>
                      <td className="py-3 px-4 text-gray-700">{note.delivery_date}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${status.color}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Link
                          to={`/operations/delivery-notes/${note.id}`}
                          className="text-[#1F2D3D] hover:underline text-sm font-medium"
                        >
                          View Details
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
